import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { API } from "@/lib/api";
import { useSiteContent } from "@/contexts/SiteContent";
import Logo from "@/components/Logo";

const SESSION_KEY = "ma_chat_session";

function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `s_${Math.random().toString(36).slice(2)}${Date.now()}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export default function Chatbot() {
  const content = useSiteContent();
  const cfg = content?.chatbot || {
    greeting: "Welcome to AZEFAC Properties. I'm Aria — your private concierge. May I help you explore a residence, a neighbourhood, or the Dubai market?",
    agent_name: "Aria",
    agent_title: "AI Concierge",
  };

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: cfg.greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSession());

  useEffect(() => {
    // If greeting changes (admin edit), reset the seed message
    setMessages((prev) =>
      prev.length && prev[0].role === "assistant"
        ? [{ role: "assistant", text: cfg.greeting }, ...prev.slice(1)]
        : [{ role: "assistant", text: cfg.greeting }]
    );
  }, [cfg.greeting]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("network");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last?.role === "assistant") {
              copy[copy.length - 1] = { ...last, text: (last.text || "") + data };
            }
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "I'm unable to reach the concierge desk right now. Please try again in a moment.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating toggle — cream, gold accents */}
      <button
        data-testid="chatbot-toggle-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chat"
        className={`fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-[#f5f0e8] border border-[#c5a059] flex items-center justify-center shadow-[0_10px_28px_rgba(28,28,28,0.18)] hover:bg-[#c5a059] transition-all ${
          open ? "" : "pulse-ring"
        }`}
      >
        {open ? (
          <X size={18} className="text-[#1c1c1c]" />
        ) : (
          <Sparkles size={20} className="text-[#c5a059]" />
        )}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed inset-x-4 bottom-24 sm:inset-x-auto sm:right-6 z-[60] sm:w-[420px] max-w-[calc(100vw-2rem)] h-[580px] max-h-[80vh] bg-[#f5f0e8] border border-[#c5a059]/25 shadow-[0_24px_64px_rgba(28,28,28,0.18)] flex flex-col"
        >
          {/* Header — cream with brand */}
          <div className="px-6 py-5 border-b border-[#1c1c1c]/10 flex items-center justify-between bg-[#f5f0e8]">
            <div className="flex items-center gap-3">
              <Logo size="sm" tone="dark" />
              <div className="hidden sm:block h-8 w-px bg-[#1c1c1c]/15 mx-1" />
              <div>
                <div className="font-display text-lg text-[#1c1c1c] leading-tight">
                  {cfg.agent_name}
                </div>
                <div className="text-[9px] uppercase tracking-[0.28em] text-[#3d2b1f] mt-0.5">
                  {cfg.agent_title}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#c5a059] animate-pulse" />
              <span className="text-[9px] uppercase tracking-[0.28em] text-[#3d2b1f]">Online</span>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto px-5 py-6 space-y-6 bg-[#f5f0e8]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-message-${m.role}-${i}`}
                className={`max-w-[86%] ${m.role === "user" ? "ml-auto" : ""}`}
              >
                <div
                  className={`text-[9px] uppercase tracking-[0.28em] mb-1.5 ${
                    m.role === "user" ? "text-right text-[#3d2b1f]" : "text-[#c5a059]"
                  }`}
                >
                  {m.role === "user" ? "You" : cfg.agent_name}
                </div>
                <div
                  className={`text-[14px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#c5a059] text-[#1c1c1c] px-4 py-3 font-body"
                      : "text-[#1c1c1c] border-l border-[#c5a059] pl-4 font-body"
                  }`}
                >
                  {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-[#1c1c1c]/10 px-4 py-3 flex items-center gap-3 bg-[#f5f0e8]">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about a residence, a market, an idea…"
              className="flex-1 bg-transparent text-[#1c1c1c] placeholder:text-[#3d2b1f]/50 text-[14px] outline-none px-2 py-2 font-body"
            />
            <button
              data-testid="chatbot-send"
              type="submit"
              disabled={busy || !input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-[#c5a059] text-[#1c1c1c] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
